/**
 * Client chiamate edge onboarding-public (funnel senza login).
 */
import { supabase } from "@/integrations/supabase/client";
import { getEdgeFunctionErrorMessage } from "@/lib/edge-function-error";
import type {
  OnboardingBillingPeriod,
  OnboardingCallStatus,
  OnboardingPaymentMethod,
  OnboardingStep,
  OnboardingStatus,
  OnboardingUnlockMode,
} from "@/types/onboarding";

export interface OnboardingPublicPayload {
  onboarding: {
    id: string;
    status: OnboardingStatus;
    current_step: OnboardingStep;
    price_amount: number;
    setup_fee_amount: number;
    payment_method?: OnboardingPaymentMethod;
    billing_period: OnboardingBillingPeriod;
    billing_years?: number;
    currency: string;
    expires_at: string;
    terms_accepted_at: string | null;
    payment_succeeded_at?: string | null;
    setup_fee_received_at?: string | null;
    unlock_mode?: OnboardingUnlockMode | null;
    call_status?: OnboardingCallStatus | null;
    call_booked_at?: string | null;
    call_scheduled_at?: string | null;
    calendar_url?: string | null;
    /** Canone + bonifico (se previsto) confermati → login disponibile */
    access_ready?: boolean;
    /** True se il PDF contratto è già in storage (edge aggiornata) */
    has_contract_pdf?: boolean;
    /** Tenant in prova: già ha credenziali; a pagamento → accesso illimitato */
    tenant_is_trial?: boolean;
    tenant_name: string;
    signer_name: string | null;
    signer_email: string | null;
  };
  cgs: {
    id: string;
    version: string;
    effective_date: string;
    body_html: string;
    allegati_html: string;
    /** Testo vincolante in inglese (art. 49) */
    body_html_en?: string;
    allegati_html_en?: string;
    art50_clauses: string[];
    /** Elenco clausole art. 50 nel testo vincolante inglese */
    art50_clauses_en?: string[];
  } | null;
}

async function invokeOnboardingPublic<T>(
  action: string,
  body?: Record<string, unknown>,
): Promise<T> {
  const { data, error } = await supabase.functions.invoke("onboarding-public", {
    body: { action, ...(body ?? {}) },
  });
  if (error || (data && typeof data === "object" && "error" in data && data.error)) {
    throw new Error(await getEdgeFunctionErrorMessage(error, data));
  }
  return data as T;
}

export async function fetchOnboardingByToken(token: string): Promise<OnboardingPublicPayload> {
  return invokeOnboardingPublic<OnboardingPublicPayload>("get", { token });
}

export async function acceptOnboardingTerms(token: string): Promise<{
  ok: boolean;
  already_accepted?: boolean;
  current_step: string;
  redirect: string;
}> {
  return invokeOnboardingPublic("accept-terms", {
    token,
    flag_cgs_accepted: true,
    flag_art50_accepted: true,
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
  });
}

/** Scarica PDF contratto (solo dopo accettazione T&C). */
export async function downloadOnboardingContractPdf(
  token: string,
  lang: "en" | "it" = "en",
): Promise<{ url: string }> {
  const data = await invokeOnboardingPublic<{ url: string }>("download-contract", {
    token,
    lang,
  });
  if (!data?.url) throw new Error("URL PDF non disponibile");
  return { url: data.url };
}

/** Crea o riusa sessione checkout Whop per l'onboarding (solo canone ricorrente). */
export async function createOnboardingWhopCheckout(
  token: string,
  opts?: { forceNew?: boolean },
): Promise<{
  session_id: string;
  plan_id: string | null;
  already_paid?: boolean;
  reused?: boolean;
  redirect?: string;
  environment?: "sandbox" | "production";
}> {
  const data = await invokeOnboardingPublic<{
    session_id: string;
    plan_id: string | null;
    already_paid?: boolean;
    reused?: boolean;
    redirect?: string;
    environment?: "sandbox" | "production";
  }>("create-checkout", {
    token,
    force_new: !!opts?.forceNew,
    return_origin: typeof window !== "undefined" ? window.location.origin : "",
  });
  if (!data?.session_id && !data?.already_paid) {
    throw new Error("Sessione checkout Whop non disponibile");
  }
  return data;
}

/** Registra subito il canone carta dopo onComplete Whop (non aspetta solo il webhook). */
export async function confirmOnboardingCardPayment(
  token: string,
  opts?: { paymentId?: string | null; membershipId?: string | null },
): Promise<{
  ok: boolean;
  payment_succeeded: boolean;
  already?: boolean;
  awaiting_setup_fee?: boolean;
  awaiting_webhook?: boolean;
  unlocked?: boolean;
  code?: string;
  message?: string;
}> {
  return invokeOnboardingPublic("confirm-card-payment", {
    token,
    payment_id: opts?.paymentId || null,
    membership_id: opts?.membershipId || null,
    app_origin: typeof window !== "undefined" ? window.location.origin : "",
  });
}

export async function listOnboardingCallSlots(token: string): Promise<{
  slots: string[];
  timezone: string;
  slot_minutes: number;
}> {
  return invokeOnboardingPublic("list-call-slots", { token });
}

export async function bookOnboardingCall(
  token: string,
  scheduledAt: string,
): Promise<{
  call_status: string;
  call_scheduled_at?: string | null;
  awaiting_setup_fee?: boolean;
}> {
  return invokeOnboardingPublic("book-call", {
    token,
    scheduled_at: scheduledAt,
  });
}

export async function skipOnboardingCall(token: string): Promise<{
  call_status: string;
  awaiting_setup_fee?: boolean;
}> {
  return invokeOnboardingPublic("skip-call", { token });
}

export async function listOnboardingCallBookings(): Promise<{
  bookings: Array<{
    id: string;
    tenant_id: string;
    tenant_name: string;
    call_status: string | null;
    call_outcome: "done" | "no_show" | "cancelled" | null;
    call_scheduled_at: string | null;
    call_booked_at: string | null;
    signer_name: string | null;
    signer_email: string | null;
  }>;
}> {
  return invokeOnboardingPublic("list-call-bookings", {});
}

export async function fetchOnboardingCallRules(): Promise<{
  rules: import("@/lib/onboarding-call-rules").OnboardingCallRules;
}> {
  return invokeOnboardingPublic("get-call-rules", {});
}

export async function saveOnboardingCallRules(
  rules: import("@/lib/onboarding-call-rules").OnboardingCallRules,
): Promise<{ rules: import("@/lib/onboarding-call-rules").OnboardingCallRules }> {
  return invokeOnboardingPublic("set-call-rules", { rules });
}

export async function setOnboardingCallOutcome(
  onboardingId: string,
  outcome: "done" | "no_show" | "cancelled" | "clear",
): Promise<{ call_outcome: string | null }> {
  return invokeOnboardingPublic("set-call-outcome", {
    onboarding_id: onboardingId,
    outcome,
  });
}

export async function fetchOnboardingSettings(): Promise<{ onboarding_calendar_url: string }> {
  return invokeOnboardingPublic("get-onboarding-settings", {});
}

export async function saveOnboardingSettings(calendarUrl: string): Promise<{
  onboarding_calendar_url: string;
}> {
  return invokeOnboardingPublic("set-onboarding-settings", {
    onboarding_calendar_url: calendarUrl,
  });
}

export async function bulkUpdateWhopPrices(input: {
  tenantIds: string[];
  renewalPrice: number;
  billingPeriod: OnboardingBillingPeriod;
  billingYears?: number;
}): Promise<{
  updated: number;
  failed: number;
  results: Array<{ tenant_id: string; ok: boolean; error?: string; plan_id?: string; product_id?: string }>;
}> {
  return invokeOnboardingPublic("bulk-update-whop-prices", {
    tenant_ids: input.tenantIds,
    renewal_price: input.renewalPrice,
    billing_period: input.billingPeriod,
    billing_years: input.billingYears ?? 1,
  });
}

/** Switch sandbox ↔ live Whop per un tenant (platform admin). */
export async function setTenantWhopEnvironment(
  tenantId: string,
  environment: "sandbox" | "production",
): Promise<{
  ok: boolean;
  whop_environment: "sandbox" | "production";
  whop_product_id: string | null;
  whop_plan_id: string | null;
  needs_plan_confirm?: boolean;
  previous: "sandbox" | "production";
}> {
  return invokeOnboardingPublic("set-tenant-whop-environment", {
    tenant_id: tenantId,
    environment,
  });
}

export interface WhopPaymentSummary {
  id: string;
  status: string;
  substatus: string | null;
  total: number | null;
  currency: string | null;
  paid_at: string | null;
  created_at: string | null;
  card_brand: string | null;
  card_last4: string | null;
  email: string | null;
  membership_id: string | null;
  plan_id: string | null;
  refunded_amount: number | null;
  billing_reason: string | null;
}

export interface WhopMembershipSummary {
  id: string;
  status: string;
  cancel_at_period_end: boolean;
  canceled_at: string | null;
  renewal_period_start: string | null;
  renewal_period_end: string | null;
  plan_id: string | null;
  product_id: string | null;
  email: string | null;
  formatted_renewal_price: string | null;
  renewal_price?: number | null;
  currency?: string | null;
  billing_period_days?: number | null;
  manage_url: string | null;
}

export interface TenantWhopBilling {
  tenant_name: string;
  environment: "sandbox" | "production";
  product_id: string | null;
  plan_id: string | null;
  renewal_price: number | null;
  memberships: WhopMembershipSummary[];
  payments: WhopPaymentSummary[];
  /** Whop non permette di leggere gli abbonamenti (es. scope mancanti sulla chiave API) */
  memberships_error?: string | null;
  /** Quanti elementi ha letto da Whop (per capire un elenco vuoto) */
  diagnostics?: {
    memberships_read: number;
    payments_read: number;
    strategy?: string;
    seen_products?: string[];
    seen_plans?: string[];
  };
}

/** Abbonamento Whop del tenant: membership + pagamenti con carta riusciti. */
export async function fetchTenantWhopBilling(
  tenantId: string,
  debug = false,
): Promise<TenantWhopBilling & { debug?: unknown }> {
  return invokeOnboardingPublic("tenant-whop-billing", { tenant_id: tenantId, debug });
}

/** Disdice il rinnovo Whop (solo amministratore completo). */
export async function cancelTenantWhopMembership(input: {
  tenantId: string;
  membershipId: string;
  mode: "at_period_end" | "immediate";
}): Promise<{ membership: WhopMembershipSummary }> {
  return invokeOnboardingPublic("cancel-tenant-whop-membership", {
    tenant_id: input.tenantId,
    membership_id: input.membershipId,
    mode: input.mode,
  });
}
